/**
 * ListDetailHeader Component
 *
 * Shows list metadata (name, description, member count) with
 * action buttons based on ownership/permissions.
 */

import { Button } from '@/components/ui/button';
import Ionicons from '@expo/vector-icons/Ionicons';
import { router } from 'expo-router';
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import type { List } from '../types';

interface ListDetailHeaderProps {
  list: List;
  isOwner: boolean;
  canEdit: boolean;
  onEdit?: () => void;
  onManageMembers?: () => void;
  onDelete?: () => void;
}

const VISIBILITY_ICONS: Record<string, string> = {
  private: 'lock-closed-outline',
  friends: 'people-outline',
  public: 'globe-outline',
};

const VISIBILITY_LABELS: Record<string, string> = {
  private: 'Private',
  friends: 'Friends',
  public: 'Public',
};

export function ListDetailHeader({
  list,
  isOwner,
  canEdit,
  onEdit,
  onManageMembers,
  onDelete,
}: ListDetailHeaderProps) {
  return (
    <View className="px-4 pt-4 pb-3">
      {/* Title row */}
      <View className="flex-row items-start">
        <View className="flex-1">
          <Text
            className="text-2xl font-bold text-foreground"
            accessibilityRole="header"
          >
            {list.name}
          </Text>
          {list.ownerName && (
            <Pressable
              onPress={() => router.push(`/(root)/profile/${list.ownerId}`)}
              hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
              accessibilityRole="link"
              accessibilityLabel={`View ${list.ownerName}'s profile`}
            >
              <Text className="text-sm mt-0.5 text-muted-foreground">
                by <Text className="text-primary">{list.ownerName}</Text>
              </Text>
            </Pressable>
          )}
          {list.description && (
            <Text className="text-sm mt-1 text-muted-foreground leading-5">
              {list.description}
            </Text>
          )}
        </View>
      </View>

      {/* Stats row */}
      <View className="flex-row items-center mt-3" style={{ gap: 12 }}>
        <View className="flex-row items-center">
          <Ionicons name="layers-outline" size={14} className="text-muted-foreground" />
          <Text className="ml-1 text-sm text-muted-foreground">
            {list.itemCount} {list.itemCount === 1 ? 'item' : 'items'}
          </Text>
        </View>
        {list.memberCount > 1 && (
          <View className="flex-row items-center">
            <Ionicons name="people-outline" size={14} className="text-muted-foreground" />
            <Text className="ml-1 text-sm text-muted-foreground">
              {list.memberCount} {list.memberCount === 1 ? 'member' : 'members'}
            </Text>
          </View>
        )}
        <View className="flex-row items-center">
          <Ionicons
            name={(VISIBILITY_ICONS[list.visibility] ?? 'globe-outline') as any}
            size={14}
            className="text-muted-foreground"
          />
          <Text className="ml-1 text-sm text-muted-foreground">
            {VISIBILITY_LABELS[list.visibility] ?? list.visibility}
          </Text>
        </View>
        {list.type === 'default' && (
          <View className="px-1.5 py-0.5 rounded bg-primary/10">
            <Text className="text-xs font-medium text-primary">Default</Text>
          </View>
        )}
      </View>

      {/* Action buttons */}
      {(canEdit || isOwner) && (
        <View className="flex-row mt-3" style={{ gap: 8 }}>
          {isOwner && list.type !== 'default' && onEdit && (
            <Button variant="outline" size="sm" onPress={onEdit}>
              <Ionicons name="pencil-outline" size={14} className="text-foreground" />
              <Text className="text-sm font-semibold text-foreground">Edit</Text>
            </Button>
          )}
          {isOwner && list.type !== 'default' && onManageMembers && (
            <Button variant="outline" size="sm" onPress={onManageMembers}>
              <Ionicons name="people-outline" size={14} className="text-foreground" />
              <Text className="text-sm font-semibold text-foreground">
                {list.memberCount > 0 ? `${list.memberCount} ${list.memberCount === 1 ? 'Member' : 'Members'}` : 'Members'}
              </Text>
            </Button>
          )}
          {isOwner && list.type !== 'default' && onDelete && (
            <Button variant="outline" size="sm" onPress={onDelete}>
              <Ionicons name="trash-outline" size={14} className="text-destructive" />
              <Text className="text-sm font-semibold text-destructive">Delete</Text>
            </Button>
          )}
        </View>
      )}
    </View>
  );
}
